import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api, logoUrl } from "../lib/api";
import { applyTheme } from "@/lib/theme";

function hexToHslTriplet(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function darken(hex, amount = 0.12) {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const f = (c) => Math.max(0, Math.round(c * (1 - amount)));
  const r = f((n >> 16) & 255), g = f((n >> 8) & 255), b = f(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

const parseHex = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const toHex = (rgb) => `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;
const luminance = ([r, g, b]) => {
  const lin = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};
const contrastBetween = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};
const mixRgb = (a, b, t) => a.map((c, i) => c + (b[i] - c) * t);

/** Tono del color de marca para TEXTO con >= 4,5:1 sobre la superficie:
 *  oscurece (claro) o aclara (oscuro) el color del propietario; el relleno no se toca. */
export function brandTextTones(hex) {
  const base = parseHex(hex);
  if (!base) return null;
  const pick = (surface, target, start) => {
    for (let t = start; t <= 1.0001; t += 0.02) {
      const c = mixRgb(base, target, t);
      if (contrastBetween(c, surface) >= 4.5) return { c, t };
    }
    return { c: target, t: 1 };
  };
  const light = pick([255, 255, 255], [0, 0, 0], 0);
  const dark = pick([28, 28, 30], [255, 255, 255], 0.4);
  return {
    light: toHex(light.c),
    lightHover: toHex(mixRgb(light.c, [0, 0, 0], 0.15)),
    dark: toHex(dark.c),
    darkHover: toHex(mixRgb(dark.c, [255, 255, 255], 0.15)),
  };
}

/** Contraste del blanco sobre el color (WCAG). < 4,5 → el texto sobre el relleno debe ser oscuro. */
function whiteContrast(hex) {
  const rgb = parseHex(hex);
  return rgb ? 1.05 / (luminance(rgb) + 0.05) : null;
}

/** Fetches public app config at bootstrap and applies DB-driven theming:
 *  app name (document.title), brand CSS variables and default language. */
export const getAppName = () => localStorage.getItem("casarent_app_name") || "CasaRent";

export default function AppConfigBoot() {
  const { i18n } = useTranslation();
  useEffect(() => {
    api.get("/public/app-config").then(({ data }) => {
      if (data.name) {
        document.title = data.name;
        const prev = localStorage.getItem("casarent_app_name");
        localStorage.setItem("casarent_app_name", data.name);
        i18n.options.interpolation = { ...i18n.options.interpolation, defaultVariables: { app: data.name } };
        // Primera visita o cambio de nombre: recarga única para que todos los
        // componentes (que leen getAppName en render) pinten la marca correcta.
        if (prev !== data.name && localStorage.getItem("casarent_app_name") === data.name) {
          window.location.replace(window.location.href);
        }
      }
      const root = document.documentElement;
      if (data.primary_color) {
        // Variables propias del propietario: index.css las usa en claro tal cual
        // y en oscuro las deriva (aclara) para mantener el contraste.
        root.style.setProperty("--brand-user", data.primary_color);
        root.style.setProperty("--brand-user-hover", darken(data.primary_color));
        const tones = brandTextTones(data.primary_color);
        if (tones) {
          root.style.setProperty("--brand-user-text", tones.light);
          root.style.setProperty("--brand-user-text-hover", tones.lightHover);
          root.style.setProperty("--brand-user-text-dark", tones.dark);
          root.style.setProperty("--brand-user-text-dark-hover", tones.darkHover);
        }
        root.setAttribute("data-brand", "custom");
        const wc = whiteContrast(data.primary_color);
        if (wc !== null && wc < 4.5) root.setAttribute("data-brand-ink", "dark");
        else root.removeAttribute("data-brand-ink");
        const hsl = hexToHslTriplet(data.primary_color);
        if (hsl) root.style.setProperty("--primary-user", hsl);
        applyTheme();
      }
      if (data.accent_color) root.style.setProperty("--brand-sage", data.accent_color);
      if (!localStorage.getItem("casarent_lang") && data.default_language) {
        i18n.changeLanguage(data.default_language);
      }
      if (data.logo_file_id) {
        const href = logoUrl(data.logo_file_id);
        const setIcon = (rel, type) => {
          let el = document.querySelector(`link[rel="${rel}"]`);
          if (!el) {
            el = document.createElement("link");
            el.rel = rel;
            document.head.appendChild(el);
          }
          if (type) el.type = type;
          el.href = href;
        };
        setIcon("icon", "image/png");
        setIcon("apple-touch-icon");
      }
    }).catch(() => {});
  }, [i18n]);
  return null;
}
